import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { HRCampaignService } from 'src/services/hr-campaign.service'
import { ECampaignType, EHRDispatchStatus } from 'src/types/hr'
import { HrPageWrapper } from '../../../_components/hr-page-wrapper.component'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import { KpiCard } from '../../_components/kpi-card.component'
import { CampaignHeader } from '../../_components/campaign-header.component'
import { AlertCampaignDispatchesTable } from '../_components/alert-campaign-dispatches-table.component'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useHRCampaignSocket } from '../../_hooks/use-hr-campaign-socket.hook'

type TAlertFilter = EHRDispatchStatus.PENDING | EHRDispatchStatus.COMPLETED | 'not_sent'

export default function AlertCampaignDetailPage() {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const { campaignId: campaignIdParam } = useParams<{ campaignId: string }>()
  const campaignId = parseInt(campaignIdParam!, 10)

  useHRCampaignSocket(campaignId)
  const [activeFilter, setActiveFilter] = useState<TAlertFilter | null>(null)

  const {
    isLoading: isLoadingCampaign,
    error: campaignError,
    data: campaign,
  } = useQuery({
    queryKey: ['hr-campaign', project.id, campaignId],
    queryFn: () => HRCampaignService.getCampaign(project.id, campaignId),
  })

  useEffect(() => {
    if (campaign && campaign.type !== ECampaignType.ALERT) {
      navigate(`/${project.id}/hr/campaigns/plan-blanc/${campaignId}`, { replace: true })
    }
  }, [campaign, campaignId, project.id, navigate])

  const {
    isLoading: isLoadingDispatches,
    error: dispatchesError,
    data: dispatches,
  } = useQuery({
    queryKey: ['hr-campaign-dispatches', project.id, campaignId],
    queryFn: () => HRCampaignService.getDispatches(project.id, campaignId),
    enabled: !!campaign,
  })

  const kpis = useMemo(() => {
    if (!dispatches) return null
    return {
      sending: dispatches.filter((d) => d.status === EHRDispatchStatus.PENDING).length,
      sent: dispatches.filter((d) => d.status === EHRDispatchStatus.COMPLETED).length,
      notSent: dispatches.filter(
        (d) => d.status !== EHRDispatchStatus.PENDING && d.status !== EHRDispatchStatus.COMPLETED,
      ).length,
    }
  }, [dispatches])

  const filteredDispatches = useMemo(() => {
    if (!dispatches || activeFilter === null) return dispatches ?? []
    if (activeFilter === EHRDispatchStatus.PENDING)
      return dispatches.filter((d) => d.status === EHRDispatchStatus.PENDING)
    if (activeFilter === EHRDispatchStatus.COMPLETED)
      return dispatches.filter((d) => d.status === EHRDispatchStatus.COMPLETED)
    return dispatches.filter(
      (d) => d.status !== EHRDispatchStatus.PENDING && d.status !== EHRDispatchStatus.COMPLETED,
    )
  }, [dispatches, activeFilter])

  const toggleFilter = (filter: TAlertFilter) => {
    setActiveFilter((prev) => (prev === filter ? null : filter))
  }

  if (campaignError || dispatchesError) {
    return <ErrorQueryHandler error={campaignError || dispatchesError} />
  }

  return (
    <HrPageWrapper
      title={
        <span className="c-flex c-items-baseline c-gap-3">
          <LocalizedLink
            to={`/${project.id}/hr/campaigns`}
            className="c-text-sm c-font-normal c-font-sans c-text-grey-500 hover:c-text-primary"
          >
            ← {t('components.navigation.hr-campaigns')}
          </LocalizedLink>
          {campaign && (
            <>
              <span className="c-text-grey-300">/</span>
              <span>
                {t('pages.hr.campaigns.detail.breadcrumb-title', {
                  type: t('pages.hr.campaigns.type.ALERT'),
                  date: getFormattedDate(campaign.sentAt),
                })}
              </span>
            </>
          )}
        </span>
      }
    >
      {isLoadingCampaign && <div className="loading" />}

      {campaign && (
        <div className="c-flex c-flex-col c-flex-1 c-min-h-0">
          <CampaignHeader campaign={campaign} />

          {isLoadingDispatches && <div className="loading" />}

          {dispatches && kpis && (
            <>
              <div className="c-grid c-grid-cols-3 c-gap-3 c-mb-6">
                <KpiCard
                  label={t('pages.hr.campaigns.detail.alert-kpis.sending')}
                  value={kpis.sending}
                  colorClass="c-text-yellow-600"
                  bgColorClass="c-bg-yellow-100"
                  onClick={() => toggleFilter(EHRDispatchStatus.PENDING)}
                  isActive={activeFilter === EHRDispatchStatus.PENDING}
                />
                <KpiCard
                  label={t('pages.hr.campaigns.detail.alert-kpis.sent')}
                  value={kpis.sent}
                  colorClass="c-text-green-600"
                  bgColorClass="c-bg-green-100"
                  onClick={() => toggleFilter(EHRDispatchStatus.COMPLETED)}
                  isActive={activeFilter === EHRDispatchStatus.COMPLETED}
                />
                <KpiCard
                  label={t('pages.hr.campaigns.detail.alert-kpis.not-sent')}
                  value={kpis.notSent}
                  colorClass="c-text-red-500"
                  bgColorClass="c-bg-red-100"
                  onClick={() => toggleFilter('not_sent')}
                  isActive={activeFilter === 'not_sent'}
                />
              </div>

              <h2 className="c-font-semibold c-font-nexa c-mb-3">
                {t('pages.hr.campaigns.detail.dispatches-title')}
              </h2>

              <div className="c-flex c-flex-col c-flex-1 c-min-h-0">
                {filteredDispatches.length === 0 && (
                  <p className="c-text-grey-500">{t('pages.hr.campaigns.detail.dispatches-empty')}</p>
                )}
                {filteredDispatches.length > 0 && (
                  <AlertCampaignDispatchesTable dispatches={filteredDispatches} campaignId={campaignId} />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </HrPageWrapper>
  )
}
